/* eslint-disable */
import { useContentStore } from "@/stores/content";
import { Bridge } from "./bridge";
import { Valve } from "./valve"
import { ChannelPointOutput, ContentUnitOutput } from "./typeDefinations";
import { computed, reactive, watch } from "vue";

export class ChannelPort {
  elementId: string;
  idx: number;
  position = [0, 0];
  isConnected = false;
  constructor(x: number, y: number, elm: string, idx = 0) {
    this.position[0] = x;
    this.position[1] = y;
    this.elementId = elm;
    this.idx = idx;
  }
}

const dummyChannelPort = new ChannelPort(0, 0, "");

export class ChannelPoint {
  position: number[];
  isConnectedToPort = false;
  connectedPort = dummyChannelPort;
  roundedRadius = 0;
  bridges = Array<Bridge>();
  valves = Array<Valve>();
  isSelected = false; // (in rounded corner mode)
  constructor(point: number[]) {
    this.position = point;
  }

  buildOutput(): ChannelPointOutput {
    const point = {
      position: this.position,
      isConnectedToPort: this.isConnectedToPort,
      roundedRadius: this.roundedRadius,
      connectedPort: {
        connectedPortElement: this.connectedPort.elementId,
        connectedPortIndex: this.connectedPort.idx
      }
    };
    return point;
  }

  // bridge's channelSeg.end will be set in channel
  // modified with valve but dont know whether that is correct
  clone(cid: string, channelWidth: number, idx: number): ChannelPoint {
    const pointCopy = new ChannelPoint(this.position.slice());
    pointCopy.roundedRadius = this.roundedRadius;
    this.bridges.forEach((b) => {
      const bridgeCopy = b.makeCopy(pointCopy, cid, channelWidth, idx);
      // add to content
      const contentStore = useContentStore();
      contentStore.addElmentToContent(bridgeCopy);
      pointCopy.bridges.push(bridgeCopy);
    });
    this.valves.forEach((v) => {
      const valveCopy = v.makeCopy(pointCopy, cid, channelWidth, idx)
      const contentStore = useContentStore();
      contentStore.addElmentToContent(valveCopy);
      pointCopy.valves.push(valveCopy);
    })
    return pointCopy;
  }

  resetBridges(): void {
    this.bridges = Array<Bridge>();
  }

  resetValves(): void {
    this.valves = Array<Valve>();
  }
}

export class ChannelPortWithSize extends ChannelPort {
  portWidth: number;
  portHeight: number;
  constructor(
    x: number,
    y: number,
    cid: string,
    idx: number,
    w: number,
    h: number
  ) {
    super(x, y, cid, idx);
    this.portWidth = w;
    this.portHeight = h;
  }
}

export class InletOutletLayers {
  layers = reactive<Array<{ layer: string; value: number; modified: boolean }>>([]);
  state = reactive({
    bottomLayer: '',
    topLayer: ''
  });

  constructor(layer: string, initialState?: Array<{ layer: string; value: number; modified: boolean }> ) {
    const contentStore = useContentStore();
    this.state.bottomLayer = layer;
    this.state.topLayer = contentStore.getTopLayerId;
    this.layers = reactive<Array<{ layer: string; value: number; modified: boolean }>>(initialState || []);
  }

  setupReactivity(parsed: boolean) {
    const updateLayers = () => {
      const intersectingLayers = this.intersectingLayers.value;
      const maxHeightValues = this.maxHeightValues.value;
      if (!parsed) {
        const updatedLayers = intersectingLayers.map((layer, index) => {
          const existingLayer = this.layers.find(l => l.layer === layer);
          // If the layer is marked as modified, retain the current value
          if (existingLayer && existingLayer.modified) {
            return existingLayer;
          } else {
            return {
              layer,
              value: maxHeightValues[index] || 0,
              modified: false, 
            };
          }
        });
        this.layers.splice(0, this.layers.length, ...updatedLayers);
      } else { 
        parsed = false;
        return this.layers; 
      }
    };
  
    watch(() => [this.intersectingLayers.value, this.maxHeightValues.value], updateLayers, {
      deep: true,
      immediate: true,
    });

  }

  get intersectingLayers() {
    return computed(() => {
      const contentStore = useContentStore();
      const topLayer = contentStore.getTopLayerId;
      return contentStore.getIntersectingLayers(this.state.bottomLayer, topLayer);
    });
  }

  get maxHeightValues() {
    return computed(() => {
      const contentStore = useContentStore();
      const layers = Array.from(contentStore.layers.keys());
      const hMaxArray = contentStore.getMaxCompHeights;
      let startIndex = -1;
      for (let i = 0; i < hMaxArray.length; i++) {
        const l = layers[i];
        if (l === this.state.bottomLayer) {
          startIndex = i;
          break;
        }
      }
      if (startIndex === -1) return [];
      else return hMaxArray.slice(startIndex);
    })
  }

  setValue(layer: string, value: number): boolean {
    const index = this.layers.findIndex(l => l.layer === layer);
    if (index === -1) {
      return false;
    }
    this.layers[index] = { ...this.layers[index], value, modified: true };
    return true;
  }
}

export class LevelConnectionLayers {
  layers = reactive<Array<{ layer: string; value: number; modified: boolean }>>([]);
  state = reactive({
    bottomLayer: '',
    topLayer: ''
  });

  constructor(bottomLayer: string, topLayer: string, initialState?: Array<{ layer: string; value: number; modified: boolean }>) {
    this.state.bottomLayer = bottomLayer;
    this.state.topLayer = topLayer;
    this.layers = reactive<Array<{ layer: string; value: number; modified: boolean }>>(initialState || []);
  }

  setupReactivity(parsed: boolean) {
    const updateLayers = () => {
      const intersectingLayers = this.intersectingLayers.value;
      const maxHeightValues = this.maxHeightValues.value;
      if (!parsed) {
        const updatedLayers = intersectingLayers.map((layer, index) => {
          const existingLayer = this.layers.find(l => l.layer === layer);
          // If the layer is marked as modified, retain the current value
          if (existingLayer && existingLayer.modified) {
            return existingLayer;
          } else {
            return {
              layer,
              value: maxHeightValues[index] || 0,
              modified: false, 
            };
          }
        });
        if (updatedLayers.length > 0) {
          updatedLayers[updatedLayers.length - 1].value = 0;
        }
        this.layers.splice(0, this.layers.length, ...updatedLayers);
      } else {
        parsed = false;
        return this.layers;
      }
    };

    watch(() => [this.intersectingLayers.value, this.maxHeightValues.value], updateLayers, {
      deep: true,
      immediate: true,
    });

    watch(() => [this.state.bottomLayer, this.state.topLayer], updateLayers, {
      immediate: true,
    });
  }

  get intersectingLayers() {
    return computed(() => {
      const contentStore = useContentStore();
      return contentStore.getIntersectingLayers(this.state.bottomLayer, this.state.topLayer);
    });
  }

  get maxHeightValues() {
    return computed(() => {
      const contentStore = useContentStore();
      const layers = Array.from(contentStore.layers.keys());
      const hMaxArray = contentStore.getMaxCompHeightsForLvlConnection;
      let startIndex = -1;
      let endIndex = -1;
      for (let i = 0; i < hMaxArray.length; i++) {
        const l = layers[i];
        if (l === this.state.bottomLayer) {
          startIndex = i;
          break;
        }
      }
      for (let j = 0; j < hMaxArray.length; j++) {
        const l = layers[j];
        if (l === this.state.topLayer) {
          endIndex = j;
          break;
        }
      }
      if (startIndex === -1 || endIndex === -1) return [];
      else return hMaxArray.slice(startIndex, endIndex);
    });
  }

  setValue(layer: string, value: number): boolean {
    const index = this.layers.findIndex(l => l.layer === layer);
    if (index === -1) {
      return false;
    }
    this.layers[index] = { ...this.layers[index], value, modified: true };
    return true;
  }

  updateTopLayer(topLayer: string) {
    this.state.topLayer = topLayer;
  }

  updateBottomLayer(bottomLayer: string) {
    this.state.bottomLayer = bottomLayer;
  }
}

export type ChannelSegment = {
  cid: string;
  idx: number;
  start: ChannelPoint;
  end: ChannelPoint;
  width: number;
};

export abstract class ContentUnitGeneral {
  static count = 0;
  layer: string;
  abstract id: string;
  abstract get type(): string;
  abstract buildOutput(layer: string): ContentUnitOutput;
  constructor(layer: string) {
    this.layer = layer;
  }
}

export abstract class ContentUnit extends ContentUnitGeneral {
  display = true;
  constructor(layer: string) {
    super(layer);
  }
}

export abstract class BasicPart extends ContentUnit {
  id = "";
  translate = [0, 0];
  // called with paste
  abstract clone(): BasicPart;
  abstract updatePosition(x: number, y: number): void;
  setTranslate(x: number, y: number): void {
    this.translate[0] = x;
    this.translate[1] = y;
  }
  resetTranslate(): void {
    this.translate[0] = this.translate[1] = 0;
  }
}

export enum ShapeType {
  Hollow,
  Solid,
  Channel
}

export enum STLLoadingState {
  Idle,
  Loading,
  Succeed,
  Fail
}

export enum Mode {
  Select,
  Channel,
  Polygon,
  Circle,
  InletOutlet,
  Bridge,
  Valve,
  RoundedCorner,
  InterLayer,
  Component
}

export enum SelectionState {
  Idle,
  IsSelectingGroup,
  EditingdSingleElement,
  EditingGroup
}

export enum MouseOperation {
  Idle,
  Selecting,
  MovingElement,
  MovingBridge,
  MovingChannelVertex,
  MovingGroup,
  RotatingComponent,
  MovingPolygonVertex,
  MovingValve
}

export enum ChannelType {
  Round,
  Square
}
/* eslint-enable */